import { Component, Injector, OnInit } from '@angular/core';
import { Validators, AbstractControl } from '@angular/forms';
import { BaseComponent } from 'src/app/common/base/base.component';
import { Configuration } from 'src/app/common/enumerations/enumerations';
import { SiteConfiguration } from 'src/app/components/site-configuration/site-configuration';
import { SiteConfigurationService } from 'src/app/components/site-configuration/site-configuration.service';

@Component({
  selector: 'scf-v2-settings-notifications',
  templateUrl: './scf-v2-settings-notifications.component.html',
  styleUrls: ['./scf-v2-settings-notifications.component.scss']
})
export class ScfV2SettingsNotificationsComponent extends BaseComponent implements OnInit {

  loading = false;
  siteConfiguration!: SiteConfiguration[];

  public get notificationActiveCtrl(): AbstractControl | null { return this.formGroup.get('notificationActiveCtrl'); }
  public get notificationEmailsCtrl(): AbstractControl | null { return this.formGroup.get('notificationEmailsCtrl'); }
  public get notificationHPEmailsCtrl(): AbstractControl | null { return this.formGroup.get('notificationHPEmailsCtrl'); }
  public get notificationRPEmailsCtrl(): AbstractControl | null { return this.formGroup.get('notificationRPEmailsCtrl'); }

  constructor(
    public siteConfService: SiteConfigurationService,
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.buildform();
    this.loadSiteConfiguration();
  }

  buildform() {
    this.formGroup = this.formBuilder.group({
      notificationActiveCtrl: [null, Validators.required],
      notificationEmailsCtrl: [null, Validators.pattern(/^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,4}(,\s)?)+$/)],
      notificationHPEmailsCtrl: [null, Validators.pattern(/^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,4}(,\s)?)+$/)],
      notificationRPEmailsCtrl: [null, Validators.pattern(/^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,4}(,\s)?)+$/)],
    });
  }

  loadSiteConfiguration() {
    this.loading = true;
    this.siteConfService.getAll().toPromise().then(data => {
      if (data) {
        this.siteConfiguration = data;
        this.loading = false;
        // US 6920: Bind the email values (not the whole config objects) and read the toggle case-insensitively
        this.notificationActiveCtrl?.setValue(this.isNotificationActive());
        this.notificationEmailsCtrl?.setValue(this.siteConfiguration.find(x => x.id == Configuration.SCFEmails)?.value ?? '');
        this.notificationHPEmailsCtrl?.setValue(this.siteConfiguration.find(x => x.id == Configuration.SCFHPEmails)?.value ?? '');
        this.notificationRPEmailsCtrl?.setValue(this.siteConfiguration.find(x => x.id == Configuration.SCFRPEmails)?.value ?? '');
        this.checkDisabled();
      }
    });
  }

  /**
   * Reads the SCF notifications flag; values may be stored as "True" or "true".
   * @returns True when SCF notifications are enabled.
   */
  isNotificationActive(): boolean {
    return String(this.siteConfiguration.find(x => x.id == Configuration.SCFNotifications)?.value ?? '').toLowerCase() == 'true';
  }

  checkDisabled() {
    if (this.isNotificationActive()) {
      this.notificationEmailsCtrl?.enable();
    }
    else {
      this.notificationEmailsCtrl?.disable();
    }
  }

  override getErrorMsg(control: AbstractControl | null): string | null {
    if (control)
      if (control.hasError('pattern')) { return 'Invalid email Addresses'; }
    return null;
  }

  activeToggleChanged(e: any) {
    // US 6920: Store "True"/"False" to match the seeded value the notification sender checks
    this.siteConfiguration.find(x => x.id == Configuration.SCFNotifications)!.value = e.checked ? 'True' : 'False';
    this.checkDisabled();
    this.save();
  }

  /**
   * Stores an edited email list and saves when the form is valid.
   * @param type 0 = all notifications, 1 = HP, 2 = RP.
   * @param e Change event from the email input.
   */
  emailsChanged(type: number, e: any) {
    // US 6920: Keep invalid email lists out of the saved configuration (a later toggle save would persist them)
    if (!this.formGroup.valid) { return; }

    switch (type) {
      case 0:
        this.siteConfiguration.find(x => x.id == Configuration.SCFEmails)!.value = e.target.value;
        break;
      case 1:
        this.siteConfiguration.find(x => x.id == Configuration.SCFHPEmails)!.value = e.target.value;
        break;
      case 2:
        this.siteConfiguration.find(x => x.id == Configuration.SCFRPEmails)!.value = e.target.value;
        break;
    }

    this.save();
  }

  getHint(type: number) {
    switch (type) {
      case 0:
        return this.getMessage('SCFEmailNotificationslAll').description;
        break;
      case 1:
        return this.getMessage('SCFEmailNotificationslHP').description;
        break;
      case 2:
        return this.getMessage('SCFEmailNotificationslRP').description;
        break;
    }
    return '';
  }

  /**
   * Saves the SCF notification settings. Only the four SCF keys are sent so other site
   * configuration values are not overwritten with the copy loaded when this tab opened.
   */
  save() {
    const scfConfigs: number[] = [Configuration.SCFNotifications, Configuration.SCFEmails, Configuration.SCFHPEmails, Configuration.SCFRPEmails];
    const saveConfigs = this.siteConfiguration.filter(c => scfConfigs.includes(c.id)).map(c => {
      return { id: c.id, value: c.value?.toString() ?? '' };
    });
    this.siteConfService.updateAll(saveConfigs).subscribe({
      next: () => this.alert.success('Configuration Saved!'),
      error: (err) => {
        console.error('updateAll failed', err);
        this.alert.defaultError();
      }
    });
  }
}
